import { FragmentType, getFragmentData } from '@/__generated__';
import { OrderStatus, PaymentStatus, UpdatePaymentDocument } from '@/__generated__/graphql';
import { DeletePaymentDocument } from '@/features/payment/graphql/queries';
import { useMutation } from '@apollo/client';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useInputState } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconCheck,
  IconMinus,
  IconPlus,
  IconPrinter,
  IconRefresh,
  IconTrash,
  IconTruckDelivery,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { orderDetailsFragment } from '../graphql/fragments';
import {
  cancelOrderDocument,
  createShipmentDPDDocument,
  processOrderDocument,
  recalculateOrderTotalDocument,
  updateOrderDocument,
} from '../graphql/mutations';

const orderStatusTranslations = {
  [OrderStatus.Created]: 'Vytvorená',
  [OrderStatus.Confirmed]: 'Potvrdená',
  [OrderStatus.Processed]: 'Spracovaná',
  [OrderStatus.Shipped]: 'Odoslaná',
  [OrderStatus.Delivered]: 'Doručená',
  [OrderStatus.Cancelled]: 'Zrušená',
  [OrderStatus.Reviewing]: 'V riešení',
  [OrderStatus.Returned]: 'Vrátená',
  [OrderStatus.Internal]: 'Interná',
  [OrderStatus.Reserved]: 'Rezervovaná',
};

const orderStatusColors = {
  [OrderStatus.Created]: 'gray',
  [OrderStatus.Confirmed]: 'blue',
  [OrderStatus.Processed]: 'cyan',
  [OrderStatus.Shipped]: 'teal',
  [OrderStatus.Delivered]: 'green',
  [OrderStatus.Cancelled]: 'red',
  [OrderStatus.Reviewing]: 'orange',
  [OrderStatus.Returned]: 'gray',
  [OrderStatus.Internal]: 'dark',
  [OrderStatus.Reserved]: 'indigo',
};

const paymentStatusColors = {
  [PaymentStatus.Pending]: 'gray',
  [PaymentStatus.Review]: 'orange',
  [PaymentStatus.Failed]: 'red',
  [PaymentStatus.Completed]: 'green',
};

const paymentStatusTranslations = {
  [PaymentStatus.Pending]: 'Čakajúca',
  [PaymentStatus.Review]: 'Nespárovaná',
  [PaymentStatus.Failed]: 'Zlyhala',
  [PaymentStatus.Completed]: 'Zaplatená',
};

// const ShippingModal = ({ orderId }: { orderId: string }) => {
//   const [position, setPosition] = useState(1);
//   return (
//     <Stack>
//       <Alert color="orange">Ak má objednávka viac balíkov, tlač cez DPD!</Alert>
//       <Grid>
//         <Grid.Col span={6}>
//           <Button onClick={() => setPosition(1)} disabled={position === 1}>
//             1
//           </Button>
//         </Grid.Col>
//         <Grid.Col span={6}>
//           <Button onClick={() => setPosition(2)} disabled={position === 2}>
//             2
//           </Button>
//         </Grid.Col>
//         <Grid.Col span={6}>
//           <Button onClick={() => setPosition(3)} disabled={position === 3}>
//             3
//           </Button>
//         </Grid.Col>
//         <Grid.Col span={6}>
//           <Button onClick={() => setPosition(4)} disabled={position === 4}>
//             4
//           </Button>
//         </Grid.Col>
//       </Grid>
//       <Button
//         component="a"
//         href={`https://api.laskykrasky.sk/api/gls/?orderId=${orderId}&position=${position}`}
//         target="_blank"
//       >
//         Vytlačíť štítok
//       </Button>
//     </Stack>
//   );
// };

const CreateShipmentModal = ({
  orderId,
  packageNumber,
  name,
  total,
}: {
  orderId: string;
  packageNumber: number;
  name: string;
  total: number;
}) => {
  const [count, setCount] = useState(1);

  const [createShipmentDPD, { loading }] = useMutation(createShipmentDPDDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted: () => {
      modals.closeAll();

      window.open(`https://api.laskykrasky.sk/api/dpd/?orderId=${orderId}`, '_blank')?.focus();
    },
  });

  return (
    <Stack>
      <Alert color="orange" mx="auto">
        <Text ta="center" fw={500}>
          Skontroluj či sedí! POZOR! TOTO JE DPD!
        </Text>
        <Title ta="center" fw={500}>
          {packageNumber} - {name} ({total} €)
        </Title>
      </Alert>
      <Group mx="auto">
        <ActionIcon onClick={() => setCount(count - 1)} disabled={count === 1}>
          <IconMinus size={16} />
        </ActionIcon>
        <Text>{count}</Text>
        <ActionIcon onClick={() => setCount(count + 1)}>
          <IconPlus size={16} />
        </ActionIcon>
      </Group>
      <Button
        loading={loading}
        onClick={() => {
          void createShipmentDPD({ variables: { orderId, count } });
        }}
      >
        Vytvoriť štítok DPD
      </Button>
    </Stack>
  );
};

const OrderPrintModal = ({ orderId, employee }: { orderId: string; employee?: string | null }) => {
  const [_employee, setEmployee] = useInputState<string | undefined>(employee ?? undefined);

  return (
    <Stack>
      <Alert c="orange">
        Kto bude baliť túto objednávku? Ak tu už meno je, znamená to, že už bola niekym zabalená!
      </Alert>
      <TextInput
        value={_employee}
        onChange={setEmployee}
        title="Zamestnanec"
        description="Napíš meno..."
      />
      <Button
        disabled={!_employee}
        component="a"
        href={`https://api.laskykrasky.sk/api/order/print?${new URLSearchParams({
          orderId,
          ...(_employee && { employee: _employee }),
        })}`}
        target="_blank"
      >
        Vytlačiť objednávku
      </Button>
    </Stack>
  );
};

const OrderCancelModal = ({
  orderId,
  package: packageNumber,
}: {
  orderId: string;
  package: number;
}) => {
  const [cancelOrder, { loading }] = useMutation(cancelOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [reason, setReason] = useInputState<string | undefined>(
    `Nedokončenie objednávky č. ${packageNumber}.`
  );

  return (
    <Stack>
      <Alert c="red">Naozaj chcete zrušiť objednávku?</Alert>
      <Button
        color="orange"
        loading={loading}
        onClick={() => {
          cancelOrder({ variables: { orderId } });
        }}
      >
        Zrušiť objednávku
      </Button>
      <Divider />
      <TextInput
        value={reason}
        onChange={setReason}
        title="Dôvod zablokovania"
        description="Prečo bude zákazník zablokovaný?"
        placeholder="Napr. Nedokončenie objednávky, nezaplatenie a pod."
      />
      <Button
        color="red"
        disabled={!reason}
        loading={loading}
        onClick={() => {
          modals.closeAll();

          modals.openConfirmModal({
            title: 'Zrušiť objednávku',
            children: `Naozaj chcete zrušiť objednávku č. ${packageNumber}, a zablokovať objednávanie zákazníkovi z dôvodu: ${reason}?`,
            onConfirm: () => {
              cancelOrder({ variables: { orderId, reason } });
            },
            labels: { cancel: 'Nie', confirm: 'Áno, zrušiť a zablokovať!' },
          });
        }}
      >
        Zrušiť a zablokovať!
      </Button>
    </Stack>
  );
};

export const OrderDetailsHeader = (props: { order: FragmentType<typeof orderDetailsFragment> }) => {
  const order = getFragmentData(orderDetailsFragment, props.order);

  const [recalculateOrderTotal, { loading }] = useMutation(recalculateOrderTotalDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [processOrder, processOrderMutation] = useMutation(processOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [updateOrder, updateOrderMutation] = useMutation(updateOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [updatePayment, updatePaymentMutation] = useMutation(UpdatePaymentDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [deletePayment, deletePaymentMutation] = useMutation(DeletePaymentDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  return (
    <Paper p="md" withBorder component={Group} justify="space-between">
      <Group>
        <Text size="xl" fw={500}>
          {order.package}
        </Text>
        <Box>
          <Text fw={500}>{order.id}</Text>
          <Text size="sm" c="dimmed">
            {dayjs(order.createdAt).format('DD MMM YYYY HH:mm')}
          </Text>
          {order.packedBy && (
            <Text size="sm" c="dimmed">
              Zabalil: {order.packedBy}
            </Text>
          )}
        </Box>
      </Group>
      {order.trackingNumber && (
        <Group>
          <Text>{order.trackingNumber}</Text>
          <ActionIcon
            color="orange"
            onClick={() =>
              modals.openConfirmModal({
                size: 'auto',
                title: 'Vytlačiť znovu',
                children: (
                  <Alert color="orange">
                    <Text fz="xl" fw={500}>
                      DPD {order.package} - {order.customer.name} ({order.total} €)
                    </Text>
                    <Text fw={500}>
                      POZOR! Môže ísť o duplicitný štítok! Štítok {order.package} (
                      {order.trackingNumber}) už bol vytvorený! Chceš ho vytlačiť znovu?
                    </Text>
                  </Alert>
                ),
                labels: { cancel: 'Nie', confirm: 'Vytlačiť' },
                onConfirm: () =>
                  window
                    .open(`https://api.laskykrasky.sk/api/dpd/?orderId=${order.id}`, '_blank')
                    ?.focus(),
              })
            }
          >
            <IconPrinter size={16} />
          </ActionIcon>
        </Group>
      )}

      <Badge color={orderStatusColors[order.status]}>{orderStatusTranslations[order.status]}</Badge>

      <Group>
        <Text>{order.total} €</Text>
        <ActionIcon
          onClick={() => recalculateOrderTotal({ variables: { orderId: order.id } })}
          loading={loading}
        >
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>
      {(order.payments?.length ?? 0) > 0 &&
        order.payments?.map((payment) => (
          <Stack key={payment.id}>
            <Group>
              <Text>
                {payment.amount} ({payment.method})
              </Text>
              {payment.status !== PaymentStatus.Completed && (
                <ActionIcon
                  color="green"
                  onClick={() =>
                    updatePayment({
                      variables: {
                        where: { id: payment.id },
                        data: { status: { set: PaymentStatus.Completed } },
                      },
                    })
                  }
                  loading={updatePaymentMutation.loading}
                >
                  <IconCheck size={16} />
                </ActionIcon>
              )}
              <ActionIcon
                color="red"
                onClick={() =>
                  modals.openConfirmModal({
                    title: 'Zmazať platbu',
                    children: 'Naozaj chcete zmazať platbu?',
                    onConfirm: () => {
                      void deletePayment({ variables: { where: { id: payment.id } } });
                    },
                    labels: { cancel: 'Nie', confirm: 'Áno' },
                  })
                }
                loading={deletePaymentMutation.loading}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
            <Badge color={paymentStatusColors[payment.status]}>
              {paymentStatusTranslations[payment.status]}
            </Badge>
            {payment.referenceId && <Text c="dimmed">{payment.referenceId}</Text>}
            {payment.updatedAt && (
              <Text c="dimmed">{dayjs(payment.updatedAt).format('DD MMM YYYY HH:mm')}</Text>
            )}
          </Stack>
        ))}
      <Group>
        {order.status === OrderStatus.Created && (
          <Button size="xs" component="a" href={`/checkout/${order.id}`} target="_blank">
            Dokončiť za zákazníka
          </Button>
        )}
        {order.status === OrderStatus.Confirmed && (
          <Button
            size="xs"
            color="teal"
            onClick={() => {
              void processOrder({ variables: { orderId: order.id } });
            }}
            loading={processOrderMutation.loading}
          >
            Zabaliť objednávku
          </Button>
        )}
        {(order.status === OrderStatus.Processed || order.status === OrderStatus.Confirmed) &&
          order.paymentMethod?.id !== 'cm4bow5qp0009n82su9fyiisa' && (
            <Group>
              <ActionIcon
                disabled={
                  (order.paymentMethod?.name === 'Prevod' ||
                    order.paymentMethod?.name === 'Karta') &&
                  (order.payments.some((p) => p.status !== PaymentStatus.Completed) ||
                    order.payments.length === 0)
                }
                color="teal"
                onClick={() => {
                  modals.open({
                    title: 'Vytvoriť zásielku',
                    children: (
                      <CreateShipmentModal
                        orderId={order.id}
                        packageNumber={order.package}
                        name={order.customer.name}
                        total={order.total}
                      />
                    ),
                  });
                }}
              >
                <IconTruckDelivery size={16} />
              </ActionIcon>
            </Group>
          )}
        <Stack gap="xs">
          {order.status === OrderStatus.Reviewing && (
            <Button
              size="xs"
              color="green"
              loading={updateOrderMutation.loading}
              onClick={() =>
                void updateOrder({
                  variables: {
                    where: { id: order.id },
                    data: { status: { set: OrderStatus.Confirmed } },
                  },
                })
              }
            >
              Vyriešené
            </Button>
          )}
          {order.status === OrderStatus.Confirmed && (
            <Button
              size="xs"
              color="orange"
              loading={updateOrderMutation.loading}
              onClick={() =>
                modals.openConfirmModal({
                  title: 'Problém s objednávkou',
                  children: 'Naozaj chcete označiť objednávku ako problém?',
                  onConfirm: () => {
                    void updateOrder({
                      variables: {
                        where: { id: order.id },
                        data: { status: { set: OrderStatus.Reviewing } },
                      },
                    });
                  },
                  labels: { cancel: 'Nie', confirm: 'Áno' },
                })
              }
            >
              Problém
            </Button>
          )}
          <Button
            size="xs"
            color="red"
            onClick={() => {
              modals.open({
                title: 'Zrušiť objednávku',
                children: <OrderCancelModal orderId={order.id} package={order.package} />,
              });
            }}
          >
            Zrušiť
          </Button>
          <ActionIcon
            color="orange"
            onClick={() =>
              modals.open({
                children: <OrderPrintModal orderId={order.id} employee={order.packedBy} />,
              })
            }
          >
            <IconPrinter size={16} />
          </ActionIcon>
        </Stack>
      </Group>
    </Paper>
  );
};
